import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Receipt, RefreshCw, Truck } from 'lucide-react';
import { INSTALLATION_STATUS, INVOICE_STATUS, TALLY_STATUS, dateLabel } from '@adisys/shared';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { Fact, MetaBadge, Notice, Section, errorText, inr2, type TabProps } from '../../../lib/crm';
import { Button, Card, EmptyState, Field, Input, Modal, Select, Skeleton, StatusBadge, Textarea, useToast } from '../../../components/ui';
import { DispatchModal } from '../finance/DispatchModal';
import { InvoiceModal } from '../finance/InvoiceModal';

export function FinanceTab({ project, reload }: TabProps) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const order = project.summary?.order;
  const seeFinance = can('crm.finance.view');
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['crm'] }); reload(); };

  const challans = useQuery({
    queryKey: ['crm', 'order-challans', order?.id], enabled: !!order && seeFinance,
    queryFn: () => api.get(`/crm/orders/${order.id}/challans`),
  });
  const invoices = useQuery({
    queryKey: ['crm', 'invoices', { projectId: project.id }], enabled: !!order && seeFinance,
    queryFn: () => api.get('/crm/invoices', { projectId: project.id, size: 100 }),
  });

  const [dispatch, setDispatch] = useState(false);
  const [invoice, setInvoice] = useState(false);
  const [receiveFor, setReceiveFor] = useState<any>(null);
  const [receiver, setReceiver] = useState('');
  const [instOpen, setInstOpen] = useState(false);
  const [inst, setInst] = useState({ status: project.installationStatus ?? 'not_started', note: '' });
  const [handOpen, setHandOpen] = useState(false);
  const [handNote, setHandNote] = useState('');
  const [problem, setProblem] = useState('');

  const delivered = useMutation({
    mutationFn: () => api.post(`/crm/challans/${receiveFor.id}/delivered`, { receivedByName: receiver }),
    onSuccess: () => { toast.success('Delivery confirmed'); setReceiveFor(null); setReceiver(''); refresh(); },
    onError: (e) => toast.error('Could not confirm', errorText(e)),
  });
  const install = useMutation({
    mutationFn: () => api.post(`/crm/projects/${project.id}/installation`, { status: inst.status, note: inst.note || undefined }),
    onSuccess: () => { toast.success('Installation updated'); setInstOpen(false); refresh(); },
    onError: (e) => setProblem(errorText(e)),
  });
  const handover = useMutation({
    mutationFn: () => api.post(`/crm/projects/${project.id}/handover`, { note: handNote }),
    onSuccess: () => { toast.success('Project handed over'); setHandOpen(false); refresh(); },
    onError: (e) => setProblem(errorText(e)),
  });
  const piSync = useMutation({
    mutationFn: (id: string) => api.post(`/crm/quotations/${id}/tally`),
    onSuccess: (r: any) => r.status === 'synced' ? toast.success('Proforma sent to Tally')
      : r.status === 'queued' ? toast.info('Queued', 'No Tally endpoint is configured.') : toast.error('Tally rejected it', r.error ?? undefined),
    onError: (e) => toast.error('Tally sync failed', errorText(e)),
  });

  if (!order) {
    return <EmptyState icon={<Receipt className="h-5 w-5" />} title="Nothing to dispatch or invoice yet"
      description="Dispatch, invoicing and handover begin once the customer approves a quotation and the order is confirmed." />;
  }

  const b = project.summary.billing ?? { billed: 0, paid: 0 };
  const outstanding = Number(b.billed) - Number(b.paid);
  const manage = can('crm.finance.manage');
  const done = project.status === 'completed';
  const approvedPi = (project.summary.quotations ?? []).find((q: any) => q.kind === 'proforma' && q.status === 'approved');

  return (
    <div className="space-y-6">
      <Card>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Fact label="Order">{order.orderNo}</Fact>
          <Fact label="Order value">{inr2(order.grandTotal)}</Fact>
          <Fact label="Billed">{inr2(b.billed)}</Fact>
          <Fact label="Collected">{inr2(b.paid)}</Fact>
          <Fact label="Outstanding"><span className={outstanding > 0 ? 'font-semibold text-ink-900' : ''}>{inr2(outstanding)}</span></Fact>
        </dl>
        <p className="mt-3 text-xs text-ink-500">Delivered {Number(order.delivered)} of {Number(order.units)} units
          {Number(order.secured) > Number(order.delivered) && ` · ${Number(order.secured) - Number(order.delivered)} in stock for this order`}</p>
        {manage && !done && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button icon={<Truck className="h-4 w-4" />} onClick={() => setDispatch(true)}>Dispatch goods</Button>
            <Button icon={<Receipt className="h-4 w-4" />} onClick={() => setInvoice(true)}>Raise invoice</Button>
          </div>)}
      </Card>

      {seeFinance && (
        <>
          <Section title="Delivery challans" description="Partial deliveries are normal; each dispatch gets its own challan.">
            {challans.isLoading ? <Skeleton className="h-16 w-full" /> : (challans.data?.data ?? []).length === 0 ? (
              <p className="text-sm text-ink-500">Nothing dispatched yet.</p>) : (
              <ul className="divide-y divide-line rounded-card bg-card ring-1 ring-line">
                {challans.data.data.map((c: any) => (
                  <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                    <div><p className="font-medium text-ink-900">{c.challanNo}</p>
                      <p className="text-xs text-ink-500">{dateLabel(c.dispatchDate)} · {Number(c.units)} units{c.transporter ? ` · ${c.transporter}` : ''}{c.vehicleNo ? ` · ${c.vehicleNo}` : ''}</p></div>
                    <div className="flex items-center gap-2">
                      <StatusBadge tone={c.status === 'delivered' ? 'success' : 'progress'}>{c.status === 'delivered' ? 'Delivered' : 'In transit'}</StatusBadge>
                      <Link to={`/crm/finance/challans/${c.id}/document`} target="_blank"><Button size="sm" icon={<FileText className="h-3.5 w-3.5" />}>Print</Button></Link>
                      {manage && c.status !== 'delivered' && <Button size="sm" onClick={() => setReceiveFor(c)}>Confirm delivery</Button>}
                    </div>
                  </li>))}
              </ul>)}
          </Section>

          <Section title="Invoices">
            {invoices.isLoading ? <Skeleton className="h-16 w-full" /> : (invoices.data?.data ?? []).length === 0 ? (
              <p className="text-sm text-ink-500">No invoice raised yet.</p>) : (
              <ul className="divide-y divide-line rounded-card bg-card ring-1 ring-line">
                {invoices.data.data.map((i: any) => (
                  <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                    <div><Link className="font-medium text-ink-900 hover:underline" to={`/crm/finance/invoices/${i.id}`}>{i.invoiceNo}</Link>
                      <p className="text-xs text-ink-500">{dateLabel(i.invoiceDate)} · due {dateLabel(i.dueDate)} · {inr2(i.grandTotal)} · paid {inr2(i.paidAmount)}</p></div>
                    <div className="flex items-center gap-2"><MetaBadge meta={TALLY_STATUS} value={i.tallyStatus} />
                      <MetaBadge meta={INVOICE_STATUS} value={i.status} /></div>
                  </li>))}
              </ul>)}
            {approvedPi && manage && (
              <Button size="sm" icon={<RefreshCw className="h-3.5 w-3.5" />} loading={piSync.isPending} onClick={() => piSync.mutate(approvedPi.id)}>
                Sync approved proforma {approvedPi.quoteNo} to Tally</Button>)}
          </Section>
        </>
      )}

      <Section title="Installation & handover">
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1 text-sm">
              <p className="flex items-center gap-2">Installation: <MetaBadge meta={INSTALLATION_STATUS} value={project.installationStatus} /></p>
              {project.installationNote && <p className="text-xs text-ink-500">{project.installationNote}</p>}
              {project.handoverAt && <p className="text-xs text-success">Handed over {dateLabel(project.handoverAt)}. {project.handoverNote}</p>}
            </div>
            {!done && can('crm.execution.update') && (
              <div className="flex gap-2">
                <Button onClick={() => { setProblem(''); setInstOpen(true); }}>Update installation</Button>
                <Button variant="success" icon={<CheckCircle2 className="h-4 w-4" />} onClick={() => { setProblem(''); setHandOpen(true); }}>Customer handover</Button>
              </div>)}
          </div>
        </Card>
      </Section>

      <DispatchModal open={dispatch} orderId={order.id} onClose={() => setDispatch(false)} onDone={refresh} />
      <InvoiceModal open={invoice} orderId={order.id} onClose={() => setInvoice(false)} onDone={refresh} />

      <Modal open={!!receiveFor} onClose={() => setReceiveFor(null)} size="sm" title="Confirm delivery"
        description={receiveFor?.challanNo}
        footer={<><Button onClick={() => setReceiveFor(null)}>Cancel</Button>
          <Button variant="primary" loading={delivered.isPending} disabled={receiver.trim().length < 2} onClick={() => delivered.mutate()}>Confirm</Button></>}>
        <Field label="Received by (customer representative)" required>
          <Input value={receiver} onChange={(e) => setReceiver(e.target.value)} /></Field>
      </Modal>

      <Modal open={instOpen} onClose={() => setInstOpen(false)} size="sm" title="Installation progress"
        footer={<><Button onClick={() => setInstOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={install.isPending} onClick={() => install.mutate()}>Save</Button></>}>
        <div className="space-y-3">
          {problem && <Notice tone="danger">{problem}</Notice>}
          <Field label="Status"><Select value={inst.status} onChange={(e) => setInst({ ...inst, status: e.target.value })}>
            {INSTALLATION_STATUS.list.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</Select></Field>
          <Field label="Note"><Textarea rows={3} value={inst.note} onChange={(e) => setInst({ ...inst, note: e.target.value })} /></Field>
        </div>
      </Modal>

      <Modal open={handOpen} onClose={() => setHandOpen(false)} size="sm" title="Customer handover"
        description="Everything must be delivered and the installation completed."
        footer={<><Button onClick={() => setHandOpen(false)}>Cancel</Button>
          <Button variant="success" loading={handover.isPending} disabled={handNote.trim().length < 5} onClick={() => handover.mutate()}>Record handover</Button></>}>
        <div className="space-y-3">
          {problem && <Notice tone="danger">{problem}</Notice>}
          <Field label="Handover note" required hint="For example, who signed the completion certificate.">
            <Textarea rows={3} value={handNote} onChange={(e) => setHandNote(e.target.value)} /></Field>
        </div>
      </Modal>
    </div>
  );
}
